import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { tenantsApi } from "../services/api";

const TenantContext = createContext(null);

const SELECTED_TENANT_KEY = "rocket_tronc_selected_tenant";

/**
 * Extract data from API response (handles both casing conventions)
 */
const extractData = (response) => {
  if (!response) return { success: false, data: null };
  const success = response.success ?? response.Success ?? false;
  const data = response.data ?? response.Data ?? null;
  const message = response.message ?? response.Message ?? null;
  return { success, data, message };
};

export const TenantProvider = ({ children }) => {
  const {
    user,
    isGlobalAdmin,
    isAuthenticated,
    loading: authLoading,
  } = useAuth();

  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenantState] = useState(null);
  const [loading, setLoading] = useState(true); // Start true so AppLayout shows loading until tenant resolves
  const [error, setError] = useState(null);

  // Load tenants when user is authenticated as GlobalAdmin
  useEffect(() => {
    console.log("[TenantContext] Effect triggered:", {
      authLoading,
      isAuthenticated,
      isGlobalAdmin,
      hasTenantId: !!user?.tenantId,
    });

    // Wait for auth to finish loading
    if (authLoading) {
      console.log("[TenantContext] Auth still loading, waiting...");
      return;
    }

    if (isAuthenticated && isGlobalAdmin) {
      console.log("[TenantContext] Loading tenants for Global Admin");
      loadTenants();
    } else if (isAuthenticated && user?.tenantId) {
      console.log(
        "[TenantContext] Loading current tenant for user:",
        user.email,
      );
      loadCurrentTenant();
    } else {
      console.log(
        "[TenantContext] Not authenticated or no tenant, clearing state",
      );
      setTenants([]);
      setSelectedTenantState(null);
      setLoading(false);
    }
  }, [isAuthenticated, isGlobalAdmin, user?.tenantId, authLoading]);

  const loadTenants = useCallback(async () => {
    console.log("[TenantContext] Loading all tenants...");
    setLoading(true);
    setError(null);

    try {
      const response = await tenantsApi.getAll();
      const { success, data } = extractData(response);

      if (success && data) {
        console.log("[TenantContext] Loaded tenants:", data.length);
        setTenants(data);

        // If we have a selected tenant, update it with fresh data
        if (selectedTenant) {
          const updatedTenant = data.find((t) => t.id === selectedTenant.id);
          if (updatedTenant) {
            setSelectedTenantState(updatedTenant);
          }
        } else {
          // Auto-select: restore from localStorage or pick the only tenant
          const storedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);
          if (storedTenantId) {
            const storedTenant = data.find((t) => t.id === storedTenantId);
            if (storedTenant) {
              console.log(
                "[TenantContext] Restored tenant from localStorage:",
                storedTenant.name,
              );
              setSelectedTenantState(storedTenant);
            } else if (data.length > 0) {
              // Stored tenant not found, auto-select first
              console.log(
                "[TenantContext] Stored tenant not found, auto-selecting first tenant:",
                data[0].name,
              );
              setSelectedTenantState(data[0]);
              localStorage.setItem(SELECTED_TENANT_KEY, data[0].id);
            }
          } else if (data.length > 0) {
            // No stored selection, auto-select first
            console.log(
              "[TenantContext] No stored selection, auto-selecting first tenant:",
              data[0].name,
            );
            setSelectedTenantState(data[0]);
            localStorage.setItem(SELECTED_TENANT_KEY, data[0].id);
          }
        }
      } else {
        console.log("[TenantContext] No tenants returned");
        setTenants([]);
      }
    } catch (err) {
      console.error("[TenantContext] Failed to load tenants:", err);
      if (err.status !== 401) {
        setError(err.message || "Failed to load tenants");
      }
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant]);

  const loadCurrentTenant = useCallback(async () => {
    if (!user?.tenantId) {
      console.log("[TenantContext] No tenant ID for user");
      setLoading(false);
      return;
    }

    console.log("[TenantContext] Loading current tenant:", user.tenantId);
    setLoading(true);
    setError(null);

    try {
      // Try getById first, fall back to getCurrent
      let response = await tenantsApi.getById(user.tenantId);
      let { success, data } = extractData(response);

      if (!success || !data) {
        console.log("[TenantContext] getById failed, trying getCurrent...");
        response = await tenantsApi.getCurrent();
        ({ success, data } = extractData(response));
      }

      if (success && data) {
        console.log("[TenantContext] Loaded current tenant:", data.name);
        setTenants([data]);
        setSelectedTenantState(data);
      } else {
        console.warn("[TenantContext] Could not load tenant for user");
      }
    } catch (err) {
      console.error("[TenantContext] Failed to load current tenant:", err);
      // Only set error if it's not an auth error (401)
      if (err.status !== 401) {
        setError(err.message || "Failed to load tenant");
      }
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId]);

  const setSelectedTenant = useCallback((tenant) => {
    console.log(
      "[TenantContext] Setting selected tenant:",
      tenant?.name || "null",
    );
    setSelectedTenantState(tenant);
    if (tenant) {
      localStorage.setItem(SELECTED_TENANT_KEY, tenant.id);
    } else {
      localStorage.removeItem(SELECTED_TENANT_KEY);
    }
  }, []);

  const clearSelectedTenant = useCallback(() => {
    console.log("[TenantContext] Clearing selected tenant");
    setSelectedTenantState(null);
    localStorage.removeItem(SELECTED_TENANT_KEY);
  }, []);

  const refreshTenants = useCallback(async () => {
    console.log("[TenantContext] Refreshing tenants...");
    if (isGlobalAdmin) {
      await loadTenants();
    } else {
      await loadCurrentTenant();
    }
  }, [isGlobalAdmin, loadTenants, loadCurrentTenant]);

  /**
   * Refresh a specific tenant's data (useful after updates)
   */
  const refreshTenant = useCallback(
    async (tenantId) => {
      console.log("[TenantContext] Refreshing specific tenant:", tenantId);
      try {
        const response = await tenantsApi.getById(tenantId);
        const { success, data } = extractData(response);

        if (success && data) {
          // Update the tenant in our list
          setTenants((prev) => {
            const updated = prev.map((t) => (t.id === tenantId ? data : t));
            // Add if not found
            if (!prev.some((t) => t.id === tenantId)) {
              updated.push(data);
            }
            return updated;
          });

          // Update selected tenant if it's the one we refreshed
          if (selectedTenant?.id === tenantId) {
            setSelectedTenantState(data);
          }

          return data;
        }
      } catch (err) {
        console.error("[TenantContext] Failed to refresh tenant:", err);
      }
      return null;
    },
    [selectedTenant]
  );

  /**
   * Called after creating a new tenant - adds it to the list and optionally selects it
   */
  const addTenant = useCallback(
    (newTenant, autoSelect = false) => {
      console.log("[TenantContext] Adding new tenant:", newTenant.name);
      setTenants((prev) => {
        // Check if already exists
        if (prev.some((t) => t.id === newTenant.id)) {
          return prev.map((t) => (t.id === newTenant.id ? newTenant : t));
        }
        return [...prev, newTenant];
      });

      if (autoSelect) {
        setSelectedTenant(newTenant);
      }
    },
    [setSelectedTenant]
  );

  // Get current tenant (the one to use for API calls)
  // Priority: selectedTenant (for global admin) > user's tenant
  const currentTenant =
    selectedTenant ||
    (user?.tenantId ? tenants.find((t) => t.id === user.tenantId) : null);

  // Check if tenant selection is required (for global admins)
  const needsTenantSelection =
    isGlobalAdmin && !loading && tenants.length > 0 && !selectedTenant;

  // Check if user has access to multiple tenants
  const hasMultipleTenants = tenants.length > 1;

  // Check if user has any tenants
  const hasTenants = tenants.length > 0;

  // Get current tenant ID (for API calls)
  const currentTenantId = selectedTenant?.id ?? user?.tenantId ?? null;

  // Check if viewing a different tenant than user's own
  const isViewingDifferentTenant =
    isGlobalAdmin && selectedTenant && selectedTenant.id !== user?.tenantId;

  // Get the user's role for permission checks
  const userRole = user?.role ?? null;

  const value = {
    // State
    tenants,
    selectedTenant,
    currentTenant,
    currentTenantId,
    loading,
    error,

    // Computed
    needsTenantSelection,
    hasMultipleTenants,
    hasTenants,
    isGlobalAdmin,
    isViewingDifferentTenant,
    userRole,

    // Actions
    setSelectedTenant,
    clearSelectedTenant,
    refreshTenants,
    refreshTenant,
    addTenant,
  };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

/**
 * Hook to access tenant context
 */
export const useTenantContext = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenantContext must be used within a TenantProvider");
  }
  return context;
};

export default TenantContext;
